import React, { useState, useEffect } from 'react';
import { Job, DroneMission, User, MissionTemplate } from '../types';
import { Icons } from '../constants';

interface Props {
  jobs: Job[];
  missions: DroneMission[];
  onRequestMission: (mission: DroneMission) => boolean | void;
  user?: User;
  initialServiceType?: string;
  missionTemplates?: MissionTemplate[];
}

const ClientDroneView: React.FC<Props> = ({ jobs, missions, onRequestMission, user, initialServiceType, missionTemplates = [] }) => {
  const [selectedService, setSelectedService] = useState<MissionTemplate | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  const [manualAddress, setManualAddress] = useState<string>('');
  const [isManualAddress, setIsManualAddress] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  // New detailed configuration state
  const [customerNotes, setCustomerNotes] = useState('');
  const [flightAltitude, setFlightAltitude] = useState<number>(100);
  const [resolution, setResolution] = useState<'Standard' | 'High' | '4K'>('High');
  const [deliverables, setDeliverables] = useState<string[]>(['Raw Video']);

  useEffect(() => {
    if (initialServiceType && missionTemplates.length > 0) {
      const service = missionTemplates.find(s => s.name === initialServiceType);
      if (service) {
        setSelectedService(service);
        setShowForm(true);
      }
    }
  }, [initialServiceType, missionTemplates]);

  useEffect(() => {
    if (user?.address) {
      setManualAddress(user.address);
      if (jobs.length === 0) {
        setSelectedJobId(`MANUAL-${user.address}`);
      }
    }
  }, [user, jobs.length]);

  // Filter missions to only show those related to the client's jobs or manual addresses
  const clientJobIds = jobs.map(j => j.id);
  const clientMissions = missions.filter(m => clientJobIds.includes(m.jobId) || m.jobId.startsWith('MANUAL-'));

  const handleRequest = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedService || (!selectedJobId && !manualAddress)) return;

    const newMission: DroneMission = {
      id: `DRN-${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
      jobId: isManualAddress ? `MANUAL-${manualAddress}` : selectedJobId,
      droneId: 'pending', // Will be assigned by staff
      status: 'Pending Approval',
      missionType: selectedService.name as any,
      aerialMedia: [],
      price: selectedService.basePrice,
      customerNotes,
      flightAltitude,
      resolution,
      deliverables
    };

    const success = onRequestMission(newMission);
    
    if (success !== false) {
      setShowForm(false);
      setSelectedService(null);
      setSelectedJobId('');
      setManualAddress('');
      setIsManualAddress(false);
      setCustomerNotes('');
      setFlightAltitude(100);
      setResolution('High');
      setDeliverables(['Raw Video']);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-fade-in">
      {/* Header */}
      <div className="bg-bark p-12 rounded-[48px] text-white flex flex-col md:flex-row justify-between items-center relative overflow-hidden shadow-2xl">
        <div className="relative z-10 text-center md:text-left">
          <div className="flex items-center space-x-3 mb-4 justify-center md:justify-start">
            <div className="w-10 h-10 bg-gold rounded-xl flex items-center justify-center text-bark">
              <Icons.Drone />
            </div>
            <span className="text-xl font-black uppercase tracking-widest text-gold">Aerial Intelligence</span>
          </div>
          <h2 className="text-4xl font-black mb-2 leading-tight">Drone Services</h2>
          <p className="text-stone-400 font-bold text-lg italic tracking-tight mb-8 max-w-xl">
            Elevate your property care with our fleet of advanced drones. Request a flight today.
          </p>
          <button 
            onClick={() => setShowForm(true)}
            className="px-8 py-4 bg-gold text-bark font-black rounded-2xl hover:bg-white transition shadow-xl shadow-gold/10 uppercase tracking-widest text-sm"
          >
            Request a Flight
          </button>
        </div>
        <div className="absolute right-0 bottom-0 opacity-5 transform translate-x-10 translate-y-10 scale-150 pointer-events-none text-gold">
           <div className="w-96 h-96 border-[40px] border-current rounded-full" />
        </div>
      </div>

      {showForm ? (
        <div className="bg-white rounded-[40px] border border-stone-200 p-10 shadow-sm">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-2xl font-black text-bark">Configure Your Flight</h3>
            <button onClick={() => setShowForm(false)} className="text-stone-400 hover:text-bark font-bold text-sm uppercase tracking-widest">Cancel</button>
          </div>

          <form onSubmit={handleRequest} className="space-y-8">
            <div>
              <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-4">Select Service Type</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {missionTemplates.map(service => (
                  <div 
                    key={service.name}
                    onClick={() => setSelectedService(service)}
                    className={`p-6 rounded-3xl border-2 cursor-pointer transition-all ${
                      selectedService?.name === service.name 
                      ? 'border-gecko bg-gecko/5 shadow-lg shadow-gecko/10' 
                      : 'border-stone-100 hover:border-stone-300 bg-white'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-black text-bark text-lg">{service.name}</h4>
                      <span className="font-black text-palm">${service.basePrice}</span>
                    </div>
                    <p className="text-sm text-stone-500 font-medium">{service.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="block text-xs font-black text-stone-400 uppercase tracking-widest">Select Property</label>
                <button 
                  type="button"
                  onClick={() => setIsManualAddress(!isManualAddress)}
                  className="text-xs font-bold text-palm hover:text-gecko transition-colors"
                >
                  {isManualAddress ? 'Select Existing Property' : 'Enter Address Manually'}
                </button>
              </div>
              
              {isManualAddress ? (
                <input 
                  type="text"
                  required
                  placeholder="Enter full property address..."
                  className="w-full p-4 bg-stone-50 rounded-2xl border border-stone-200 focus:ring-2 focus:ring-gecko outline-none transition font-medium"
                  value={manualAddress}
                  onChange={e => setManualAddress(e.target.value)}
                />
              ) : (
                <select 
                  required
                  className="w-full p-4 bg-stone-50 rounded-2xl border border-stone-200 focus:ring-2 focus:ring-gecko outline-none transition font-medium"
                  value={selectedJobId}
                  onChange={e => setSelectedJobId(e.target.value)}
                >
                  <option value="">Select a property...</option>
                  {user?.address && !jobs.some(j => j.address === user.address) && (
                    <option value={`MANUAL-${user.address}`}>{user.address} (My Address)</option>
                  )}
                  {jobs.map(job => (
                    <option key={job.id} value={job.id}>{job.address}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Detailed Configuration Section */}
            <div className="bg-stone-50 p-6 rounded-3xl border border-stone-200 space-y-6">
              <h4 className="font-black text-bark text-lg mb-4">Flight Configuration</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Flight Altitude (ft)</label>
                  <p className="text-xs text-stone-500 mb-2">Higher altitude covers more area, lower provides more detail.</p>
                  <input 
                    type="range" 
                    min="50" max="400" step="50"
                    className="w-full accent-gecko"
                    value={flightAltitude}
                    onChange={e => setFlightAltitude(parseInt(e.target.value))}
                  />
                  <div className="text-center font-bold text-bark mt-2">{flightAltitude} ft</div>
                </div>

                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Camera Resolution</label>
                  <select 
                    className="w-full p-3 bg-white rounded-xl border border-stone-200 focus:ring-2 focus:ring-gecko outline-none font-medium"
                    value={resolution}
                    onChange={e => setResolution(e.target.value as any)}
                  >
                    <option value="Standard">Standard (1080p) - Good for general overview</option>
                    <option value="High">High (2.7K) - Recommended for inspections</option>
                    <option value="4K">Ultra HD (4K) - Best for detailed analysis (+$50)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Requested Deliverables</label>
                <div className="flex flex-wrap gap-2">
                  {['Raw Video', 'High-Res Photos', 'Orthomosaic Map', '3D Model', 'AI Health Report'].map(item => (
                    <label key={item} className={`cursor-pointer px-4 py-2 rounded-xl border text-sm font-bold transition-colors ${deliverables.includes(item) ? 'bg-palm text-white border-palm shadow-md' : 'bg-white text-stone-500 border-stone-200 hover:border-stone-300'}`}>
                      <input 
                        type="checkbox" 
                        className="hidden"
                        checked={deliverables.includes(item)}
                        onChange={(e) => {
                          if (e.target.checked) setDeliverables([...deliverables, item]);
                          else setDeliverables(deliverables.filter(d => d !== item));
                        }}
                      />
                      {item}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Specific Instructions / Areas of Concern</label>
                <textarea 
                  rows={3}
                  placeholder="e.g., Please focus on the large oak tree near the power lines..."
                  className="w-full p-4 bg-white rounded-2xl border border-stone-200 focus:ring-2 focus:ring-gecko outline-none transition font-medium resize-none"
                  value={customerNotes}
                  onChange={e => setCustomerNotes(e.target.value)}
                />
              </div>
            </div>

            <button 
              type="submit" 
              disabled={!selectedService || (isManualAddress ? !manualAddress : !selectedJobId)}
              className="w-full py-5 bg-gecko text-white font-black rounded-2xl hover:bg-palm transition shadow-xl shadow-gecko/20 disabled:opacity-50 disabled:shadow-none transform active:scale-[0.98] uppercase tracking-widest text-sm"
            >
              Submit Flight Request
            </button>
          </form>
        </div>
      ) : (
        <div className="space-y-6">
          <h3 className="text-2xl font-black text-bark px-4">Your Flight History</h3>
          {clientMissions.length === 0 ? (
            <div className="bg-white rounded-[40px] border border-stone-200 p-12 text-center">
              <div className="w-20 h-20 bg-stone-50 rounded-3xl flex items-center justify-center mx-auto mb-6 text-stone-300">
                <Icons.Drone />
              </div>
              <p className="text-stone-400 font-black text-lg tracking-tight uppercase">No flights requested yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {clientMissions.map(mission => {
                const job = jobs.find(j => j.id === mission.jobId);
                const displayAddress = mission.jobId.startsWith('MANUAL-') 
                  ? mission.jobId.replace('MANUAL-', '') 
                  : job?.address || 'Unknown Address';
                  
                return (
                  <div key={mission.id} className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm relative overflow-hidden group">
                    <div className="flex justify-between items-start mb-6 relative z-10">
                      <div>
                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                          mission.status === 'Completed' ? 'bg-green-100 text-green-700' : 
                          mission.status === 'Denied' ? 'bg-red-100 text-red-700' :
                          mission.status === 'Pending Approval' ? 'bg-orange-100 text-orange-700' : 
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {mission.status}
                        </span>
                        <h4 className="font-black text-xl text-bark mt-3">{mission.missionType}</h4>
                        <p className="text-sm text-stone-500 font-medium mt-1">{displayAddress}</p>
                      </div>
                      <span className="font-black text-palm text-xl">${mission.price || '--'}</span>
                    </div>
                    
                    {mission.status === 'Completed' && (
                      <div className="mt-6 space-y-4 relative z-10">
                        {mission.aiSummary && (
                          <div className="p-4 bg-stone-50 rounded-2xl border border-stone-100">
                            <p className="text-[10px] font-black text-gecko uppercase tracking-widest mb-2">AI Summary</p>
                            <p className="text-sm text-stone-600 italic">"{mission.aiSummary}"</p>
                          </div>
                        )}
                        
                        <div className="bg-white border border-stone-200 rounded-2xl p-4">
                          <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-3">Post-Flight Status</p>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-stone-500 font-bold">Report Generated</span>
                              {mission.reportGenerated ? <span className="text-gecko font-black">✓</span> : <span className="text-stone-300">—</span>}
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-stone-500 font-bold">Conference Scheduled</span>
                              {mission.conferenceScheduled ? <span className="text-gecko font-black">✓</span> : <span className="text-stone-300">—</span>}
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-stone-500 font-bold">Invoiced</span>
                              {mission.invoiced ? <span className="text-gecko font-black">✓</span> : <span className="text-stone-300">—</span>}
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-stone-500 font-bold">Paid</span>
                              {mission.paid ? <span className="text-gecko font-black">✓</span> : <span className="text-stone-300">—</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="absolute -right-4 -bottom-4 opacity-5 text-bark transform group-hover:rotate-12 group-hover:scale-110 transition-transform duration-500">
                      <div className="w-32 h-32">
                         <Icons.Drone />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientDroneView;
